import { useState } from "react";
import {
  Check, X, Clock, GitMerge, Loader2, Link2, ExternalLink, AlertTriangle, Undo2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { preconApi } from "../lib/api";

const TYPES = [
  ["itb", "Invitation to bid"], ["addendum", "Addendum"], ["date_change", "Date change"],
  ["inquiry", "Customer inquiry"], ["award_notice", "Award notice"],
  ["post_bid_question", "Post-bid question"], ["other", "Other"],
];
const REASONS = [
  ["scope", "Scope"], ["capacity", "Capacity"], ["gc_rating", "GC rating"],
  ["location", "Location"], ["other", "Other"],
];
// Keep in sync with FACILITY_TYPES in backend/precon/llm.py
export const FACILITY_TYPES = [
  "K-12 School", "Higher Education", "Hospital", "Healthcare / Clinic", "Senior Living",
  "Office", "Retail", "Restaurant", "Hospitality / Hotel", "Multifamily / Residential",
  "Government / Civic", "Public Safety", "Industrial / Manufacturing",
  "Warehouse / Distribution", "Data Center", "Laboratory / Research", "Religious",
  "Recreation / Sports", "Transportation", "Mixed-Use", "Other",
];
const FIELDS = [
  ["gc_name", "GC"], ["contact_name", "Contact"], ["contact_email", "Contact email"],
  ["project_name", "Project"], ["street", "Address"], ["city", "City"], ["state", "State"],
  ["owner", "Owner"], ["architect", "Architect"], ["architect_contact_name", "Arch. contact"],
  ["architect_contact_email", "Arch. email"], ["architect_contact_phone", "Arch. phone"],
  ["bid_due_date", "Bid due"],
  ["bid_due_time", "Due time"], ["walkthrough_at", "Walkthrough"],
  ["rfi_deadline", "RFI deadline"],
];

const Row = ({ label, children }) => (
  <div className="flex items-baseline gap-2 border-b border-slate-100 py-1.5 last:border-0">
    <span className="w-24 shrink-0 text-[10px] uppercase tracking-wide text-slate-400">{label}</span>
    <div className="min-w-0 flex-1 text-xs text-slate-700">{children}</div>
  </div>
);

export const ItbCardDrawer = ({ card, onClose, onChanged }) => {
  const [draft, setDraft] = useState({});
  const [reason, setReason] = useState("");
  const [until, setUntil] = useState("");
  const [busy, setBusy] = useState("");
  const parsed = { ...(card.parsed || {}), ...draft };

  const run = async (label, fn) => {
    setBusy(label);
    try {
      const out = await fn();
      onChanged(out);
      return out;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "That didn't work");
      return null;
    } finally {
      setBusy("");
    }
  };

  const saveField = async (key, value) => {
    setDraft((d) => ({ ...d, [key]: value }));
    await preconApi.editCard(card.id, { [key]: value });
    onChanged();
  };

  const doBid = () => run("bid", async () => {
    const out = await preconApi.bid(card.id);
    if (out.action === "review") toast.warning(out.question);
    else if (out.action === "attached") toast.success("Added as a new GC invitation");
    else toast.success("On the Bid Board");
    return out;
  });

  const doDecline = () => run("decline", async () => {
    const out = await preconApi.decline(card.id, { reason, declined_in_portal: false });
    toast.success(out.portal_link ? "Logged — decline it in the portal"
      : "Declined. A decline email is drafted for review — nothing was sent");
    return out;
  });

  return (
    <Sheet open onOpenChange={onClose}>
      <SheetContent className="flex w-full !max-w-4xl flex-col gap-0 overflow-y-auto p-0"
                    data-testid="itb-drawer">
        <SheetHeader className="border-b border-slate-200 px-5 py-4">
          <SheetTitle className="text-base" data-testid="itb-drawer-title">
            {parsed.project_name || card.raw_subject || "(no subject)"}
          </SheetTitle>
          <div className="flex flex-wrap items-center gap-2 pt-1 text-[11px] text-slate-500">
            <Select value={card.type} onValueChange={(v) =>
              run("reclassify", () => preconApi.reclassify(card.id, v))}>
              <SelectTrigger className="h-6 w-44 text-[11px]" data-testid="itb-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
            <span>{Math.round((card.type_confidence || 0) * 100)}% · {card.classifier}</span>
            <span className="rounded bg-slate-100 px-1.5 py-0.5">{card.status}</span>
            {card.sla_breach && (
              <span className="rounded bg-red-100 px-1.5 py-0.5 text-red-700"
                    data-testid="itb-drawer-sla">
                waiting {card.sla_days} business days
              </span>
            )}
          </div>
        </SheetHeader>

        {card.needs_same_job_answer && (
          <div className="border-b border-amber-200 bg-amber-50 px-5 py-3"
               data-testid="itb-same-job-panel">
            <p className="text-xs font-semibold text-amber-900">
              Same job as {card.match_candidates?.[0]?.project_name}?
            </p>
            <p className="mt-0.5 text-[11px] text-amber-800">
              Scored {card.match_candidates?.[0]?.score} on the project name. Yes adds this GC as
              another invitation on that job; No opens its own row.
            </p>
            <div className="mt-2 flex gap-2">
              <Button size="sm" className="h-7 text-xs" data-testid="itb-same-job-yes"
                      onClick={() => run("same", () => preconApi.answerSameJob(
                        card.id, card.match_candidates[0].job_id, true))}>
                <GitMerge className="mr-1 h-3 w-3" /> Yes, same job
              </Button>
              <Button size="sm" variant="outline" className="h-7 text-xs"
                      data-testid="itb-same-job-no"
                      onClick={() => run("same", () => preconApi.answerSameJob(
                        card.id, card.match_candidates[0].job_id, false))}>
                No, it's a different job
              </Button>
            </div>
          </div>
        )}

        {card.status === "unmatched" && (
          <div className="border-b border-amber-200 bg-amber-50 px-5 py-3"
               data-testid="itb-unmatched-panel">
            <p className="text-xs font-semibold text-amber-900">
              <AlertTriangle className="mr-1 inline h-3.5 w-3.5" />
              Unmatched — {card.unmatched_reason === "ambiguous"
                ? "more than one job fits" : "no bid job fits"}
            </p>
            <div className="mt-2 space-y-1">
              {(card.match_candidates || []).slice(0, 5).map((c) => (
                <button key={c.job_id} data-testid={`itb-file-to-${c.job_id}`}
                        className="flex w-full items-center justify-between rounded border border-amber-200 bg-white px-2 py-1 text-left text-[11px] hover:border-amber-400"
                        onClick={() => run("match", () => preconApi.matchToJob(card.id, c.job_id))}>
                  <span className="truncate">{c.project_name}</span>
                  <span className="text-slate-400">file here · {c.score}</span>
                </button>
              ))}
              {!(card.match_candidates || []).length && (
                <p className="text-[11px] text-amber-800">
                  Nothing on the board looks like this one yet.
                </p>
              )}
            </div>
          </div>
        )}

        <div className="grid gap-5 px-5 py-4 md:grid-cols-2">
          <div>
            <h3 className="pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              What we read
            </h3>
            {card.gc_needs_confirmation && (
              <p className="mb-1 rounded bg-amber-50 px-2 py-1 text-[11px] text-amber-800"
                 data-testid="itb-gc-guess-warning">
                The GC name was taken from the sender's email domain — confirm who this is.
              </p>
            )}
            {FIELDS.map(([key, label]) => (
              <Row key={key} label={label}>
                <Input value={parsed[key] || ""} data-testid={`itb-field-${key}`}
                       onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
                       onBlur={(e) => e.target.value !== (card.parsed || {})[key]
                         && saveField(key, e.target.value)}
                       className="h-6 border-0 border-b border-transparent px-0 text-xs shadow-none focus-visible:border-[#007BFF] focus-visible:ring-0" />
              </Row>
            ))}
            <Row label="Facility type">
              <Select value={parsed.facility_type || ""}
                      onValueChange={(v) => saveField("facility_type", v)}>
                <SelectTrigger className="h-6 w-56 border-0 border-b border-transparent px-0 text-xs shadow-none focus:ring-0 data-[state=open]:border-[#007BFF]"
                               data-testid="itb-field-facility_type">
                  <SelectValue placeholder="— set facility type —" />
                </SelectTrigger>
                <SelectContent>
                  {FACILITY_TYPES.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                </SelectContent>
              </Select>
            </Row>
            <Row label="Scopes">
              {(card.scopes_mapped || []).length
                ? (card.scopes_mapped || []).map((s) => (
                  <span key={s.scope_code} title={s.scope_name}
                        className="mr-1 rounded bg-slate-100 px-1.5 py-0.5 text-[10px]">
                    {s.scope_code}
                  </span>))
                : <span className="text-slate-400">{(parsed.scopes_raw || []).join(", ") || "—"}</span>}
            </Row>
            <Row label="Portal">
              {parsed.portal_link
                ? <a href={parsed.portal_link} target="_blank" rel="noreferrer"
                     className="text-[#007BFF] hover:underline" data-testid="itb-portal-link">
                    open in {parsed.source} <ExternalLink className="inline h-3 w-3" />
                  </a>
                : <span className="text-slate-400">none</span>}
            </Row>
            <Row label="Decline link">
              {parsed.decline_link
                ? <a href={parsed.decline_link} target="_blank" rel="noreferrer"
                     className="text-[#007BFF] hover:underline" data-testid="itb-decline-link">
                    decline in portal <ExternalLink className="inline h-3 w-3" />
                  </a>
                : <span className="text-slate-400">none — we'd draft an email</span>}
            </Row>
          </div>

          <div className="min-w-0">
            <h3 className="pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              The original email
            </h3>
            <p className="text-[11px] text-slate-500">
              {card.from_name} &lt;{card.from_email}&gt; · {(card.created_at || "").slice(0, 16)}
              {card.resend_count > 0 && ` · ${card.resend_count} resend(s) attached`}
            </p>
            <p className="mt-1 text-xs font-medium text-slate-700">{card.raw_subject}</p>
            <pre className="mt-2 max-h-72 overflow-auto whitespace-pre-wrap rounded bg-slate-50 p-2 text-[11px] leading-relaxed text-slate-600"
                 data-testid="itb-raw-body">{card.raw_body || "(no body)"}</pre>
            {!!(card.attachments || []).length && (
              <ul className="mt-2 space-y-1 text-[11px] text-slate-600">
                {card.attachments.map((a, i) => (
                  <li key={i} className="flex items-center gap-1">
                    <Link2 className="h-3 w-3 text-slate-400" />{a.filename}
                  </li>))}
              </ul>
            )}
            <h3 className="pt-4 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Trail
            </h3>
            <ul className="space-y-0.5 text-[11px] text-slate-500" data-testid="itb-events">
              {(card.events || []).map((e) => (
                <li key={e.id}>
                  <span className="text-slate-400">{(e.at || "").slice(0, 16)}</span> {e.kind}
                  {e.actor && e.actor !== "system" ? ` · ${e.actor}` : ""}
                </li>))}
            </ul>
          </div>
        </div>

        <div className="mt-auto flex flex-wrap items-center gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3">
          {["itb", "inquiry"].includes(card.type) && !card.bid_job_id && (
            <Button size="sm" className="h-8 text-xs" onClick={doBid}
                    disabled={!card.actionable || !!busy} data-testid="itb-bid-btn">
              {busy === "bid" ? <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                : <Check className="mr-1 h-3 w-3" />} Bid it
            </Button>
          )}
          {card.status === "merged" && (
            <Button size="sm" variant="outline" className="h-8 text-xs"
                    onClick={() => run("undo", () => preconApi.undoMerge(card.id))}
                    data-testid="itb-undo-merge-btn">
              <Undo2 className="mr-1 h-3 w-3" /> Undo the merge
            </Button>
          )}
          {!["declined", "other", "archived"].includes(card.status) && (
            <>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger className="h-8 w-36 text-xs" data-testid="itb-decline-reason">
                  <SelectValue placeholder="Why decline?" />
                </SelectTrigger>
                <SelectContent>
                  {REASONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button size="sm" variant="outline" className="h-8 text-xs text-red-600"
                      onClick={doDecline} disabled={!reason || !!busy}
                      data-testid="itb-decline-btn">
                <X className="mr-1 h-3 w-3" /> Decline
              </Button>
              <Input type="date" value={until} onChange={(e) => setUntil(e.target.value)}
                     className="h-8 w-36 text-xs" data-testid="itb-later-date" />
              <Button size="sm" variant="ghost" className="h-8 text-xs" disabled={!until || !!busy}
                      onClick={() => run("later", () => preconApi.later(card.id, until))}
                      data-testid="itb-later-btn">
                <Clock className="mr-1 h-3 w-3" /> Later
              </Button>
            </>
          )}
          {card.status === "later" && (
            <Button size="sm" variant="ghost" className="h-8 text-xs"
                    onClick={() => run("wake", () => preconApi.wake(card.id))}
                    data-testid="itb-wake-btn">
              Bring it back now
            </Button>
          )}
          {!card.actionable && ["itb", "inquiry"].includes(card.type) && (
            <span className="text-[11px] text-amber-700" data-testid="itb-missing-hint">
              Confirm {card.missing_fields?.join(", ")} before this can go on the board
            </span>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
